import { RuntimeFeatureFlag } from '@/feature-flags/definitions'
import { useRuntimeFeatureFlag } from '@/feature-flags/useRuntimeFeatureFlag'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Userpilot } from 'userpilot'
import { Lock, MedChronEmptyIllustration } from '@/assets/icons'
import { matterPaths } from '@/routing/paths'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import MatterRecordEmptyState from '@/matter-asset-common/asset/MatterRecordEmptyState'
import { selectMatterById } from '@/matter/store/selectors'
import { canEditMedChron } from '@/matter/utils/matter-permissions'
import { selectAddFilesStagingCountsForMatter, selectInitializeError } from '../store/selectors'
import { MedChronAssetActions } from '../store/slice'
import { kMedChronOwnerOnlyBuildMessage, kMedChronOwnerOnlyBuildOwnerMessage, kMedChronEditorBuildMessage } from '../constants'
import MatterRecordReadOnlyNotice from '@/matter-asset-common/asset/MatterRecordReadOnlyNotice'
import { useMatterOwnerName } from '@/matter/hooks/useMatterOwnerName'
import EditingLeaseNotice from '@/editing-leases/components/EditingLeaseNotice'
import { useMedChronEditingLease } from '../hooks/useMedChronEditingLease'
import { isHeldByAnotherUser } from '@/editing-leases/utils/is-held-by-another-user'

interface MedChronAssetEmptyStateProps {
  matterId: string
}

/**
 * Blank state shown when the matter has no chronology yet (Figma: chronology/blank state). Thin
 * wrapper over the shared MatterRecordEmptyState that supplies the med-chron copy, illustration, CTA, and
 * the first-generation-error banner (shown after a failed Generate).
 */
export default function MedChronAssetEmptyState(props: MedChronAssetEmptyStateProps) {
  const { matterId } = props
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const initializeError = useAppSelector((state: RootState) => selectInitializeError(state, { matterId }))
  const matter = useAppSelector((state: RootState) => selectMatterById(state, { matterId }))
  // An unresolved matter must keep the build CTA disabled until the viewer's membership is known.
  const { enabled: editorEnabled, loading: editorLoading } = useRuntimeFeatureFlag(RuntimeFeatureFlag.COLLAB_EDITOR_ROLE)
  const isReadOnly = !canEditMedChron(matter, editorEnabled && !editorLoading)
  const lease = useMedChronEditingLease()
  const leaseReadOnly = !isReadOnly && !lease.canWrite
  const heldByAnotherEditor = isHeldByAnotherUser(lease.lease)
  const needsStartEditing = leaseReadOnly && lease.enabled && !lease.initializing && !heldByAnotherEditor
  const [openingFilesForMatter, setOpeningFilesForMatter] = useState<string | null>(null)
  const showReadOnlyNotice = isReadOnly && !!matter && !editorLoading
  const buildMessage = editorEnabled && !editorLoading ? kMedChronEditorBuildMessage : kMedChronOwnerOnlyBuildMessage
  const ownerName = useMatterOwnerName(matterId)
  const stagingCounts = useAppSelector((state: RootState) => selectAddFilesStagingCountsForMatter(state, { matterId }))
  const stagingTotal = stagingCounts.pendingCount + stagingCounts.stagedCount

  // Re-run Userpilot once the empty-state lander has mounted so the feature-launch
  // announcement can match this page. The global route-change reload (routes/root.tsx)
  // fires on navigation, before this lander finishes its async asset load and paints.
  useEffect(() => {
    Userpilot.reload()
  }, [])

  useEffect(() => {
    if (!openingFilesForMatter) return
    if (openingFilesForMatter !== matterId) {
      setOpeningFilesForMatter(null)
      return
    }
    if (lease.pending) return
    setOpeningFilesForMatter(null)
    if (!isReadOnly && lease.canWrite) navigate(matterPaths.chronology.addFiles(matterId))
  }, [openingFilesForMatter, matterId, lease.pending, lease.canWrite, isReadOnly, navigate])

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {(showReadOnlyNotice || (leaseReadOnly && heldByAnotherEditor)) && (
        <div className="pt-4 px-4">
          {leaseReadOnly ? (
            <EditingLeaseNotice lease={lease.lease} shape="strip" />
          ) : (
            <MatterRecordReadOnlyNotice
              icon={<Lock />}
              headline={ownerName ? `${ownerName} is the owner of this matter.` : undefined}
              message={
                editorEnabled && !editorLoading ? kMedChronEditorBuildMessage : ownerName ? kMedChronOwnerOnlyBuildOwnerMessage : kMedChronOwnerOnlyBuildMessage
              }
            />
          )}
        </div>
      )}
      <MatterRecordEmptyState
        illustration={<MedChronEmptyIllustration className="h-10 w-[49px] shrink-0" />}
        title="Build your medical chronology"
        body="Build a complete timeline of visits, diagnoses, and procedures from your medical records, with every event cited to its source."
        ctaLabel="Add Files"
        onCtaClick={() => {
          if (needsStartEditing) {
            setOpeningFilesForMatter(matterId)
            lease.startEditing()
          } else navigate(matterPaths.chronology.addFiles(matterId))
        }}
        ctaDisabled={isReadOnly || lease.pending || (!lease.canWrite && !needsStartEditing)}
        ctaLoading={needsStartEditing && lease.pending}
        ctaTooltip={isReadOnly ? buildMessage : undefined}
        notice={
          stagingTotal > 0
            ? `${stagingTotal} ${stagingTotal === 1 ? 'file' : 'files'} ${stagingCounts.pendingCount > 0 ? 'uploading' : 'ready'} — continue in Add Files`
            : undefined
        }
        error={
          typeof initializeError === 'string'
            ? {
                headline: 'Chronology could not be generated.',
                detail: 'Please try again.',
                dismissLabel: 'Dismiss',
                onDismiss: () => dispatch(MedChronAssetActions.setInitializeError({ matterId, error: null })),
              }
            : undefined
        }
      />
    </div>
  )
}
